// The hand-drawn PaperMap as a real MapLibre style on PMTiles vector tiles
// (GUI §2 "Map"; map-and-geo-visualization §4). Cream land, hatched-wave ocean
// (approximated as a slightly darker fill until a sprite ships — map §6 says the
// solid paper base is intentional), faint topo contours, muted road casings,
// Space Mono place labels. Platform overlays (incidents, H3 blots, spread cones,
// help pins) layer on top in later C-items / A5 — this is the basemap.

import type { StyleSpecification } from "@maplibre/maplibre-gl-style-spec";
import { TOKENS } from "../design/tokens.js";
import { DEFAULT_TILES_URL, DEFAULT_MAP_CENTER, SOURCE_LAYERS, VECTOR_SOURCE_ID } from "./tile-source.js";

export interface PaperStyleOpts {
  mode?: "light" | "dark";
  /** pmtiles://… URL of the vector source. Defaults to the compose go-pmtiles origin. */
  tilesUrl?: string;
  /** Glyphs endpoint for Space Mono labels. Omitted → labels render unstyled-font / hidden (offline-safe). */
  glyphsUrl?: string;
}

export function paperMapStyle(opts: PaperStyleOpts = {}): StyleSpecification {
  const mode = opts.mode ?? "light";
  const t = TOKENS[mode];
  const tilesUrl = opts.tilesUrl ?? DEFAULT_TILES_URL;

  // ocean: one shade off the paper so a tile-less map still reads as intentional.
  const ocean = mode === "light" ? "#dcd6c4" : "#16140f";
  const landTint = t.paper2;

  const style: StyleSpecification = {
    version: 8,
    name: `Beacon PaperMap (${mode})`,
    center: DEFAULT_MAP_CENTER,
    zoom: 5,
    sources: {
      [VECTOR_SOURCE_ID]: {
        type: "vector",
        url: tilesUrl,
        attribution: "Beacon pilot extract",
      },
    },
    layers: [
      { id: "background", type: "background", paint: { "background-color": t.paper } },
      {
        id: "water",
        type: "fill",
        source: VECTOR_SOURCE_ID,
        "source-layer": SOURCE_LAYERS.water,
        paint: { "fill-color": ocean, "fill-outline-color": t.line },
      },
      {
        id: "land",
        type: "fill",
        source: VECTOR_SOURCE_ID,
        "source-layer": SOURCE_LAYERS.land,
        paint: { "fill-color": landTint },
      },
      {
        id: "contour",
        type: "line",
        source: VECTOR_SOURCE_ID,
        "source-layer": SOURCE_LAYERS.contour,
        paint: { "line-color": t.line, "line-opacity": 0.4, "line-width": 0.5 },
      },
      {
        id: "road",
        type: "line",
        source: VECTOR_SOURCE_ID,
        "source-layer": SOURCE_LAYERS.road,
        paint: {
          "line-color": t.ink3,
          "line-width": ["interpolate", ["linear"], ["zoom"], 5, 0.4, 10, 1.5, 14, 3],
        },
      },
      {
        id: "place-label",
        type: "symbol",
        source: VECTOR_SOURCE_ID,
        "source-layer": SOURCE_LAYERS.place,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["Space Mono Regular"],
          "text-transform": "uppercase",
          "text-letter-spacing": 0.1,
          "text-size": ["interpolate", ["linear"], ["zoom"], 5, 10, 10, 13],
        },
        paint: { "text-color": t.ink2, "text-halo-color": t.paper, "text-halo-width": 1 },
      },
    ],
  };

  // A symbol layer with `text-field` is only a VALID style when `glyphs` is set
  // (else maplibre rejects the whole style → blank map). Labels therefore track
  // glyph availability: with a glyphs endpoint we keep place-label and point at
  // it; without one we drop the text layer so the basemap still renders.
  if (opts.glyphsUrl) style.glyphs = opts.glyphsUrl;
  else style.layers = style.layers.filter((l) => l.id !== "place-label");
  return style;
}
